import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import usePrevious from 'hooks/usePrevious';
import { generateProjectName } from 'redux/middleware/api/creation-service/actions';
import {
  GenerateProjectNameFormality,
  GenerateProjectNameTone,
} from 'redux/middleware/api/creation-service/types';
import { putProject } from 'redux/middleware/api/video-project-management-service/actions';
import { hideModal } from 'redux/modules/modal';
import { showError } from 'redux/modules/notification';
import { pollForProjectNameGeneration } from 'redux/modules/project/actions';
import { Dispatch } from 'redux/types';
import { OnGenerateClipName } from './types';

export type UseRenameClipConfig = {
  projectId: string;
  defaultClipName: string;
};

export type UseRenameClipResult = {
  clipName: string;
  isRenamingClip: boolean;
  isGeneratingClipName: boolean;
  onChangeClipName: (newClipName: string) => void;
  onRenameClip: () => Promise<void>;
  onGenerateClipName: OnGenerateClipName;
};

export default function useRenameClip({
  projectId,
  defaultClipName,
}: UseRenameClipConfig) {
  const [updatedClipName, setUpdatedClipName] = useState(defaultClipName);
  const [isRenamingClip, setIsRenamingClip] = useState(false);
  const [isGeneratingClipName, setIsGeneratingClipName] = useState(false);

  const prevProjectId = usePrevious(projectId);

  const dispatch = useDispatch<Dispatch>();

  const handleRenameClip = useCallback(async (): Promise<void> => {
    try {
      setIsRenamingClip(true);

      await dispatch(
        putProject(projectId, undefined, updatedClipName, undefined, undefined),
      );

      dispatch(hideModal(updatedClipName));
    } catch {
      dispatch(showError('Error updating the clip name'));
    } finally {
      setIsRenamingClip(false);
    }
  }, [dispatch, projectId, updatedClipName]);

  const handleGenerateClipName = useCallback(
    async (
      formality: GenerateProjectNameFormality,
      tone: GenerateProjectNameTone,
    ): Promise<void> => {
      try {
        setIsGeneratingClipName(true);

        const {
          response: { id: newProjectNameGenerationId },
        } = await dispatch(
          generateProjectName({
            projectUuid: projectId,
            formality,
            tone,
          }),
        );

        const { generatedName, status } = await dispatch(
          pollForProjectNameGeneration(newProjectNameGenerationId),
        );
        if (status === 'error') {
          dispatch(showError('Error generating a new title'));
        } else {
          setUpdatedClipName(generatedName);
        }
      } catch {
        dispatch(showError('Something went wrong, please try again'));
      } finally {
        setIsGeneratingClipName(false);
      }
    },
    [dispatch, projectId],
  );

  if (projectId !== prevProjectId && defaultClipName !== updatedClipName) {
    setUpdatedClipName(defaultClipName);
    setIsRenamingClip(false);
    setIsGeneratingClipName(false);
  }

  return {
    clipName: updatedClipName,
    isRenamingClip,
    isGeneratingClipName,
    onChangeClipName: setUpdatedClipName,
    onRenameClip: handleRenameClip,
    onGenerateClipName: handleGenerateClipName,
  };
}
